/**
 * One choice, a few answers, shown as a row of buttons.
 *
 * UNDERNEATH IT IS A RADIO GROUP, and that is the whole design. A row of
 * <button aria-pressed> elements looks the same and is wrong in three ways a
 * screen reader user meets at once: nothing says the buttons are related,
 * nothing says choosing one un-chooses the others, and nothing announces
 * "2 of 4". Radios that share a name give all three for free, plus arrow-key
 * movement between them, plus the browser's own guarantee that exactly one is
 * checked — which is the requirement, and is better kept by the platform than
 * re-implemented in a click handler.
 *
 * The radios are clipped out of sight, never display:none. Hidden that way
 * they leave the tab order and the accessibility tree, and the row becomes a
 * set of coloured boxes that only a mouse can operate.
 */

import type { ReactNode } from 'react';
import styles from './SegmentedChoice.module.css';

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: ReactNode;
  /** Shown on hover. A place for the sentence the label is too short for. */
  description?: string;
}

export interface SegmentedChoiceProps<T extends string | number> {
  /** The group's name, as a screen reader announces it: "Review cycle". */
  legend: string;
  /** Shared by the radios, so the browser knows they are one group. Must be
   *  unique on the page — two groups with one name become one group. */
  name: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  /**
   * Legend above the row, as a form field, rather than beside it as an inline
   * setting.
   */
  stacked?: boolean;
  /** Lets a form style the legend like its other field labels. */
  legendClassName?: string;
  /** An id of explanatory text, attached to the whole group. */
  describedBy?: string;
}

export function SegmentedChoice<T extends string | number>({
  legend,
  name,
  options,
  value,
  onChange,
  disabled = false,
  stacked = false,
  legendClassName,
  describedBy,
}: SegmentedChoiceProps<T>) {
  return (
    <fieldset
      className={stacked ? `${styles.group} ${styles.stacked}` : styles.group}
      disabled={disabled}
      aria-describedby={describedBy}
    >
      <legend className={legendClassName ?? styles.legend}>{legend}</legend>
      <div className={styles.options}>
        {options.map((option) => {
          const on = option.value === value;
          return (
            <label
              key={String(option.value)}
              className={on ? `${styles.option} ${styles.on}` : styles.option}
              title={option.description}
            >
              <input
                type="radio"
                className={styles.input}
                name={name}
                value={String(option.value)}
                checked={on}
                onChange={() => onChange(option.value)}
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
